import React, { MouseEventHandler } from 'react';
import styled from 'styled-components';

const FALLBACK_COVER = '/book_image_null.png';

const toCssSize = (value?: number | string | null) => {
  if (value == null) {
    return undefined;
  }
  return typeof value === 'number' ? `${value}px` : value;
};

type CoverWrapperProps = {
  $width?: number | string;
  $height?: number | string;
  $borderRadius?: number | string;
  $boxShadow?: string;
  $marginBottom?: number | string;
  $marginRight?: number | string;
  onClick?: MouseEventHandler<HTMLDivElement>;
};

const CoverWrapper = styled.div<CoverWrapperProps>`
  position: relative;
  width: ${props => toCssSize(props.$width) || '140px'};
  height: ${props => toCssSize(props.$height) || '180px'};
  flex-shrink: 0;
  border-radius: ${props => toCssSize(props.$borderRadius) || '0'};
  box-shadow: ${props => props.$boxShadow ?? '0 2px 8px rgba(0, 0, 0, 0.12)'};
  margin-bottom: ${props => toCssSize(props.$marginBottom) || '0'};
  margin-right: ${props => toCssSize(props.$marginRight) || '0'};
  overflow: hidden;
  cursor: ${props => (props.onClick ? 'pointer' : 'default')};
  background: #eee;
`;

const CoverImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const AltOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 6px;
  box-sizing: border-box;
`;

const AltText = styled.span<{ $fontSize?: number }>`
  text-align: center;
  font-size: ${props => props.$fontSize || 13}px;
  font-weight: 600;
  line-height: 1.35;
  color: #222;
  word-break: keep-all;
  overflow-wrap: break-word;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
`;

export type BookCoverImageProps = {
  src?: string | null;
  alt?: string;
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  width?: number | string;
  height?: number | string;
  fontSize?: number;
  borderRadius?: number | string;
  boxShadow?: string;
  marginBottom?: number | string;
  marginRight?: number | string;
};

export default function BookCoverImage({
  src,
  alt = '',
  className,
  onClick,
  width = 140,
  height = 180,
  fontSize = 13,
  borderRadius = 0,
  boxShadow,
  marginBottom = 0,
  marginRight = 0,
}: BookCoverImageProps) {
  const hasSrc = src != null && String(src).trim() !== '';

  return (
    <CoverWrapper
      className={className}
      onClick={onClick}
      $width={width}
      $height={height}
      $borderRadius={borderRadius}
      $boxShadow={boxShadow}
      $marginBottom={marginBottom}
      $marginRight={marginRight}
    >
      <CoverImg
        src={hasSrc ? src! : FALLBACK_COVER}
        alt={alt || ''}
      />
      {!hasSrc && alt ? (
        <AltOverlay aria-hidden="true">
          <AltText $fontSize={fontSize}>{alt}</AltText>
        </AltOverlay>
      ) : null}
    </CoverWrapper>
  );
}
